import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { gateEntryAPI } from '../../services/api';
import WebcamEBillScanner from '../../components/common/WebcamEBillScanner';
import { 
  HiOutlineTruck, 
  HiOutlineSave, 
  HiOutlineClipboardList, 
  HiOutlineSearch, 
  HiOutlineTrash,
  HiOutlineArrowCircleDown,
  HiOutlineArrowCircleUp,
  HiOutlineClock,
  HiOutlinePhotograph,
  HiOutlineDocumentText,
  HiOutlineMail,
  HiOutlineX,
  HiOutlineEye,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineCamera,
  HiOutlineSparkles
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import DynamicFormEngine from '../../components/common/DynamicFormEngine';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { normalizeUnit, unitOptionsFor } from '../../utils/units';

const formatFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanPath = url.startsWith('/api/') ? url : (url.startsWith('/') ? `/api${url}` : `/api/${url}`);
  const apiURL = process.env.REACT_APP_API_URL;
  if (apiURL) {
    const base = apiURL.endsWith('/api') ? apiURL.slice(0, -4) : (apiURL.endsWith('/api/') ? apiURL.slice(0, -5) : apiURL);
    return `${base}${cleanPath}`;
  }
  return window.location.port
    ? `${window.location.protocol}//${window.location.hostname}:9898${cleanPath}`
    : `${window.location.origin}${cleanPath}`;
};

const GateGuardDashboard = ({ defaultType = 'inward' }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active entry type from URL or prop
  const currentEntryType = location.pathname.includes('outward') ? 'outward' : (location.pathname.includes('inward') ? 'inward' : defaultType);
  const [activeTab, setActiveTab] = useState(currentEntryType);

  useEffect(() => {
    if (location.pathname.includes('outward')) {
      setActiveTab('outward');
    } else if (location.pathname.includes('inward') || location.pathname.includes('gate-guard')) {
      setActiveTab('inward');
    }
  }, [location.pathname]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  
  // File uploads
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [materialPhotoFile, setMaterialPhotoFile] = useState(null);
  const [thirdBillPhotoFile, setThirdBillPhotoFile] = useState(null);

  // File input refs
  const invoiceFileRef = useRef(null);
  const materialPhotoRef = useRef(null);
  const thirdBillPhotoRef = useRef(null);

  // Media preview modal state
  const [previewMedia, setPreviewMedia] = useState(null); // { url, title }

  // Webcam E-Bill Scanner State
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [isAutoPrefilled, setIsAutoPrefilled] = useState(false);

  // Helper for current time (HH:MM)
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  // Form State
  const blankForm = (tab) => ({
    billNumber: '',
    billDate: '',
    vendorName: '',
    vendorContact: '',
    vendorGstin: '',
    materialType: tab === 'outward' ? 'Finished Drag Chain' : 'nylon',
    materialDescription: '',
    quantity: '',
    unit: tab === 'outward' ? 'meters' : 'kg',
    vehicleNumber: '',
    driverName: '',
    driverPhone: '',
    eWayBillNumber: '',
    totalAmount: '',
    accountsEmail: '',
    notifyAccounts: true,
    entryTiming: getCurrentTime(),
    exitTiming: getCurrentTime(),
    remarks: '',
    invoiceUrl: ''
  });
  const [form, setForm] = useState(() => blankForm(currentEntryType));
  // Where each scanned value came from (QR code, both AI models, needs checking ...)
  const [scanFieldStatus, setScanFieldStatus] = useState({});

  const handleEBillScanComplete = (scanned) => {
    setShowScannerModal(false);
    setIsAutoPrefilled(true);
    setScanFieldStatus(scanned.fieldStatus || {});

    // Every field comes from this bill; fields it doesn't have are cleared, not kept from an earlier entry
    setForm(prev => ({
      ...prev,
      billNumber: scanned.billNumber || '',
      billDate: scanned.billDate || '',
      poNumber: scanned.poNumber || '',
      poId: scanned.poId || '',
      poOrderedQuantity: scanned.poOrderedQuantity || '',
      vendorId: scanned.vendorId || '',
      vendorCode: scanned.vendorCode || '',
      vendorName: scanned.vendorName || '',
      vendorContact: scanned.vendorContact || '',
      vendorGstin: scanned.vendorGstin || '',
      // The material exactly as written on the bill; all line items go into the description
      materialType: scanned.materialType || prev.materialType,
      materialDescription: scanned.materialDescription || '',
      quantity: scanned.quantity || '',
      unit: normalizeUnit(scanned.unit) || prev.unit,
      vehicleNumber: scanned.vehicleNumber || '',
      driverName: scanned.driverName || '',
      driverPhone: scanned.driverPhone || '',
      eWayBillNumber: scanned.eWayBillNumber || '',
      totalAmount: scanned.totalAmount || '',
      hsnCode: scanned.hsnCode || '',
      ebillScanId: scanned.ebillScanId || '',
      remarks: scanned.remarks || `Auto-extracted via AI/OCR (${scanned.billNumber || 'Document'})`,
      invoiceUrl: scanned.invoiceUrl || prev.invoiceUrl
    }));

    const toVerify = Object.values(scanned.fieldStatus || {}).filter(f => f.status === 'review').map(f => f.label);
    if (toVerify.length) {
      toast(`⚠️ Form filled from ${scanned.billNumber || 'the bill'} — please verify: ${toVerify.join(', ')}`, { duration: 7000 });
    } else {
      toast.success(`✨ Inward details auto-prefilled from: ${scanned.billNumber || 'Scanned Document'}`);
    }
  };

  // Small tag next to a field label showing where the scanned value came from
  const scanTag = (key) => {
    if (!isAutoPrefilled) return null;
    const status = scanFieldStatus[key]?.status;
    const tag = {
      exact: { text: '🔒 QR', color: '#15803d', title: 'Exact value from the bill QR code' },
      master: { text: '📇 Master', color: '#15803d', title: 'From the vendor master' },
      confirmed: { text: '✔ AI', color: '#15803d', title: 'Both AI models read this value' },
      checksum: { text: '✔ AI', color: '#15803d', title: 'GSTIN check-digit verified' },
      ocr_match: { text: '✔ AI', color: '#0f766e', title: 'Confirmed against the OCR text' },
      review: { text: '⚠ Verify', color: '#b45309', title: 'AI models read different values — check the bill' }
    }[status];
    if (!tag) return null;
    return (
      <span title={tag.title} style={{ marginLeft: '6px', fontSize: '0.68rem', fontWeight: 700, color: tag.color }}>
        {tag.text}
      </span>
    );
  };

  // Reset form timing when active tab changes
  useEffect(() => {
    setIsAutoPrefilled(false);
    setScanFieldStatus({});
    setForm(prev => ({
      ...prev,
      materialType: activeTab === 'outward' ? 'Finished Drag Chain' : 'nylon',
      unit: activeTab === 'outward' ? 'meters' : 'kg',
      entryTiming: getCurrentTime(),
      exitTiming: getCurrentTime()
    }));
  }, [activeTab]);

  // Known materials (past inward entries, PO items, material master) for the Material dropdown
  const [materialOptions, setMaterialOptions] = useState([]);

  useEffect(() => {
    if (activeTab !== 'inward') return;
    gateEntryAPI.getMaterialOptions()
      .then(res => setMaterialOptions(res.data.data || []))
      .catch(err => console.error('Failed to load material options', err));
  }, [activeTab]);

  // Picking a known material also fills in the unit it is usually received in
  const handleMaterialChange = (value) => {
    const known = materialOptions.find(m => m.name.toLowerCase() === value.trim().toLowerCase());
    setForm(prev => ({ ...prev, materialType: value, ...(known ? { unit: known.unit } : {}) }));
  };

  // Dynamic Schema
  const subIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const [dynamicGateFields, setDynamicGateFields] = useState([]);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const res = await api.get(`/domain/schema/${subIndustryId}`);
        const moduleKey = activeTab === 'outward' ? 'gateGuardOutwardFields' : 'gateGuardFields';
        let moduleSchema = res.data.find(s => s.moduleKey === moduleKey);
        
        // Fallback to gateGuardFields if outward specific schema not yet defined
        if (!moduleSchema && activeTab === 'outward') {
          moduleSchema = res.data.find(s => s.moduleKey === 'gateGuardFields');
        }

        if (moduleSchema && moduleSchema.fields && moduleSchema.fields.length > 0) {
          // Adjust timing field according to tab
          const adjustedFields = moduleSchema.fields.map(f => {
            if (activeTab === 'outward' && (f.key === 'entryTiming' || f.key === 'entryTime')) {
              return { ...f, key: 'exitTiming', label: 'Exit Timing' };
            }
            if (activeTab === 'inward' && (f.key === 'exitTiming' || f.key === 'exitTime')) {
              return { ...f, key: 'entryTiming', label: 'Entry Timing' };
            }
            return f;
          });

          // Ensure timing field exists in schema
          const timingKey = activeTab === 'outward' ? 'exitTiming' : 'entryTiming';
          const hasTiming = adjustedFields.some(f => f.key === timingKey);
          if (!hasTiming) {
            adjustedFields.splice(2, 0, {
              key: timingKey,
              label: activeTab === 'outward' ? 'Exit Timing' : 'Entry Timing',
              type: 'time',
              required: true,
              placeholder: 'HH:MM'
            });
          }

          setDynamicGateFields(adjustedFields);
        } else {
          setDynamicGateFields([]);
        }
      } catch (err) {
        console.error('Failed to load dynamic domain schema', err);
      }
    };

    if (subIndustryId) {
      fetchSchema();
    }
  }, [subIndustryId, activeTab]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await gateEntryAPI.getAll({ 
        search, 
        entryType: activeTab 
      });
      setEntries(res.data.data || []);
    } catch (err) {
      toast.error(`Failed to load ${activeTab} gate entry logs`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTab]);

  const handleTabChange = (type) => {
    setActiveTab(type);
    navigate(type === 'outward' ? '/gate-outward' : '/gate-inward');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${activeTab} entry?`)) return;
    try {
      await gateEntryAPI.delete(id);
      toast.success('Gate entry deleted successfully');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete gate entry');
    }
  };

  // Send / Resend email to Accounts Team
  const handleNotifyAccounts = async (entry) => {
    const defaultEmail = entry.accountsEmail || form.accountsEmail || '';
    const email = window.prompt('Enter Accounts Team email address:', defaultEmail);
    if (!email) return;

    try {
      toast.loading('Sending outward details & photos to Accounts Team...', { id: 'notifyAcc' });
      await gateEntryAPI.notifyAccounts(entry._id, { email });
      toast.success(`📧 Outward details sent to Accounts (${email})`, { id: 'notifyAcc' });
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send notification to accounts', { id: 'notifyAcc' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('entryType', activeTab);

      Object.keys(form).forEach(key => {
        if (form[key] !== undefined && form[key] !== null && form[key] !== '') {
          formData.append(key, form[key]);
        }
      });

      // Append files
      if (invoiceFile) {
        formData.append('invoice', invoiceFile);
      }
      if (materialPhotoFile) {
        formData.append('materialPhoto', materialPhotoFile);
      }
      if (thirdBillPhotoFile) {
        formData.append('thirdBillPhoto', thirdBillPhotoFile);
      }

      await gateEntryAPI.create(formData);
      toast.success(`${activeTab === 'outward' ? 'Outward finished goods entry saved & notified to accounts!' : 'Inward entry saved!'}`);
      
      // Reset form
      setForm(blankForm(activeTab));
      setIsAutoPrefilled(false);
      setScanFieldStatus({});
      setInvoiceFile(null);
      setMaterialPhotoFile(null);
      setThirdBillPhotoFile(null);
      if (invoiceFileRef.current) invoiceFileRef.current.value = '';
      if (materialPhotoRef.current) materialPhotoRef.current.value = '';
      if (thirdBillPhotoRef.current) thirdBillPhotoRef.current.value = '';
      
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save gate entry');
    } finally {
      setSubmitting(false);
    }
  };

  const isInward = activeTab === 'inward';

  return (
    <DashboardLayout pageTitle={isInward ? "Gate Inward Operations" : "Gate Outward Operations (Finished Goods)"}>
      
      {/* Top Screen Selector Tabs */}
      <TabBar style={{ width: 'fit-content', maxWidth: '100%', marginBottom: '24px' }}>
        <TabBtn active={isInward} onClick={() => handleTabChange('inward')} icon={<HiOutlineArrowCircleDown style={{ fontSize: '1.2rem' }} />}>
          Inward Entry Screen (Raw Material)
        </TabBtn>
        <TabBtn active={!isInward} onClick={() => handleTabChange('outward')} icon={<HiOutlineArrowCircleUp style={{ fontSize: '1.2rem' }} />}>
          Outward Entry Screen (Finished Goods)
        </TabBtn>
      </TabBar>

      <div className="gate-layout">
        
        {/* Log Entry Form */}
        <div className="azure-card" style={{ padding: '24px', height: 'fit-content' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.15rem', fontWeight: 800 }}>
              {isInward ? <HiOutlineArrowCircleDown style={{ color: 'var(--primary)' }} /> : <HiOutlineTruck style={{ color: '#22c55e' }} />}
              {isInward ? 'Log Inward Raw Material' : 'Log Outward Finished Goods'}
            </h3>
            <span style={{ 
              fontSize: '0.75rem', 
              padding: '4px 10px', 
              borderRadius: '20px', 
              fontWeight: 700,
              background: isInward ? 'rgba(34, 197, 94, 0.15)' : 'rgba(59, 130, 246, 0.15)',
              color: isInward ? '#22c55e' : '#3b82f6',
              textTransform: 'uppercase'
            }}>
              {isInward ? 'Inward Gate' : 'Outward Gate Pass'}
            </span>
          </div>

          {/* Live Webcam E-Bill Scanner Widget for Inward Screen */}
          {isInward && (
            <div style={{
              background: isAutoPrefilled ? 'rgba(34,197,94,0.08)' : 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(99,102,241,0.05))',
              border: `1px solid ${isAutoPrefilled ? 'rgba(34,197,94,0.3)' : 'rgba(59,130,246,0.3)'}`,
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: isAutoPrefilled ? '#22c55e' : '#3b82f6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isAutoPrefilled ? <HiOutlineSparkles /> : <HiOutlineCamera />}
                  {isAutoPrefilled ? '✨ Inward Form Auto-Prefilled from Scan' : '📸 Live Webcam E-Bill Scanner'}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                  {isAutoPrefilled 
                    ? 'Bill No, Vehicle, Vendor & Quantities extracted.' 
                    : 'Hold paper E-Way bill or invoice in front of gate camera to auto-fill.'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {form.invoiceUrl && (
                  <button
                    type="button"
                    onClick={() => setPreviewMedia({ url: form.invoiceUrl, title: `Scanned E-Bill (${form.billNumber})` })}
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.75rem', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <HiOutlinePhotograph /> View Scan
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowScannerModal(true)}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '7px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <HiOutlineCamera style={{ fontSize: '1.1rem' }} /> {isAutoPrefilled ? 'Re-scan E-Bill' : 'Scan E-Bill (Webcam)'}
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {dynamicGateFields.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                <DynamicFormEngine
                  fields={dynamicGateFields}
                  data={form}
                  onChange={(key, value) => setForm(prev => ({ ...prev, [key]: value }))}
                />
              </div>
            ) : (
              <div className="gate-form-grid">
                
                {/* Bill / Invoice / Challan Number */}
                <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    {isInward ? 'Bill / Invoice No *' : 'Tax Invoice / Challan No *'}{scanTag('billNumber')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={isInward ? "e.g. INV-2026-001" : "e.g. INV-2026-089 / DC-001"}
                    value={form.billNumber || ''}
                    onChange={e => setForm({ ...form, billNumber: e.target.value })}
                    required
                  />
                </div>

                {/* Timing */}
                <div className="form-group" style={{ gridColumn: '2 / 3' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <HiOutlineClock /> {isInward ? 'Entry Timing *' : 'Exit Timing *'}
                  </label>
                  <input
                    type="time"
                    className="form-input"
                    value={isInward ? (form.entryTiming || getCurrentTime()) : (form.exitTiming || getCurrentTime())}
                    onChange={e => setForm({ 
                      ...form, 
                      [isInward ? 'entryTiming' : 'exitTiming']: e.target.value 
                    })}
                    required
                    style={{ borderColor: 'var(--primary)' }}
                  />
                </div>

                {/* Vendor / Customer Name */}
                <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    {isInward ? 'Vendor / Supplier Name *' : 'Customer / Destination *'}{scanTag('vendorName')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={isInward ? "e.g. Apex Polymers Ltd" : "e.g. Acme Industrial Ltd"}
                    value={form.vendorName || ''}
                    onChange={e => setForm({ ...form, vendorName: e.target.value })}
                    required
                  />
                </div>

                {/* Contact */}
                <div className="form-group" style={{ gridColumn: '2 / 3' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    {isInward ? 'Vendor Contact Phone' : 'Customer Contact Phone'}{scanTag('vendorContact')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. 9876543210"
                    value={form.vendorContact || ''}
                    onChange={e => setForm({ ...form, vendorContact: e.target.value })}
                  />
                </div>

                {isInward && (
                  <>
                    <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                        Bill Date{scanTag('billDate')}
                      </label>
                      <input
                        type="date"
                        className="form-input"
                        value={form.billDate || ''}
                        onChange={e => setForm({ ...form, billDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '2 / 3' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                        Vendor GSTIN{scanTag('vendorGstin')}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. 09ABCDE1234F1Z5"
                        maxLength={15}
                        value={form.vendorGstin || ''}
                        onChange={e => setForm({ ...form, vendorGstin: e.target.value.toUpperCase() })}
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                        E-Way Bill No{scanTag('eWayBillNumber')}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="12-digit e-way bill number"
                        value={form.eWayBillNumber || ''}
                        onChange={e => setForm({ ...form, eWayBillNumber: e.target.value })}
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '2 / 3' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                        Invoice Value (₹){scanTag('totalAmount')}
                      </label>
                      <input
                        type="number"
                        step="any"
                        className="form-input"
                        placeholder="Grand total on the bill"
                        value={form.totalAmount || ''}
                        onChange={e => setForm({ ...form, totalAmount: e.target.value })}
                      />
                    </div>
                  </>
                )}

                {/* Material Type */}
                <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    {isInward ? 'Material / Raw Material *' : 'Finished Goods Description *'}{scanTag('materialType')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={isInward ? "Select or type, e.g. Nylon 66 / Steel Rod" : "e.g. Finished Drag Chain 45x100"}
                    value={form.materialType || ''}
                    onChange={e => isInward ? handleMaterialChange(e.target.value) : setForm({ ...form, materialType: e.target.value })}
                    list={isInward ? 'gate-material-options' : undefined}
                    autoComplete="off"
                    required
                  />
                  {isInward && (
                    <datalist id="gate-material-options">
                      {materialOptions.map(m => (
                        <option key={m.name} value={m.name}>{m.unit}</option>
                      ))}
                    </datalist>
                  )}
                </div>

                {/* Quantity & Unit */}
                <div className="form-group" style={{ gridColumn: '2 / 3', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ flex: '1 1 110px', minWidth: 0 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                      Quantity *{scanTag('quantity')}
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="form-input"
                      placeholder="0.00"
                      value={form.quantity || ''}
                      onChange={e => setForm({ ...form, quantity: e.target.value })}
                      required
                    />
                  </div>
                  <div style={{ flex: '1 1 110px', minWidth: 0 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                      Unit
                    </label>
                    <select
                      className="form-input"
                      value={normalizeUnit(form.unit) || (isInward ? 'kg' : 'meters')}
                      onChange={e => setForm({ ...form, unit: e.target.value })}
                    >
                      {unitOptionsFor(form.unit).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Vehicle Number */}
                <div className="form-group" style={{ gridColumn: '1 / 2' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Vehicle Number *{scanTag('vehicleNumber')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. DL 01 AB 1234"
                    value={form.vehicleNumber || ''}
                    onChange={e => setForm({ ...form, vehicleNumber: e.target.value })}
                    required
                  />
                </div>

                {/* Driver Name */}
                <div className="form-group" style={{ gridColumn: '2 / 3' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Driver Name{isInward ? '' : ' *'}{scanTag('driverName')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Driver's Full Name"
                    value={form.driverName || ''}
                    onChange={e => setForm({ ...form, driverName: e.target.value })}
                    required={!isInward}
                  />
                </div>

                {/* Driver Phone Number (required for outward) */}
                <div className="form-group" style={{ gridColumn: '1 / 3' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <HiOutlinePhone /> Driver Phone Number{isInward ? '' : ' *'}{scanTag('driverPhone')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. +91 98765 43210"
                    value={form.driverPhone || ''}
                    onChange={e => setForm({ ...form, driverPhone: e.target.value })}
                    required={!isInward}
                  />
                </div>

                {/* Photo of Material & Photo of 3rd Bill Copy (Outward Specific) */}
                {!isInward ? (
                  <>
                    <div className="form-group" style={{ gridColumn: '1 / 2', background: 'rgba(59,130,246,0.04)', padding: '10px', borderRadius: '8px', border: '1px dashed rgba(59,130,246,0.3)' }}>
                      <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <HiOutlinePhotograph /> 📸 Material / Vehicle Photo
                      </label>
                      <input
                        type="file"
                        ref={materialPhotoRef}
                        className="form-input"
                        style={{ fontSize: '0.75rem', padding: '4px' }}
                        onChange={e => setMaterialPhotoFile(e.target.files?.[0] || null)}
                        accept="image/*,video/*"
                      />
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Photo of loaded vehicle/material</span>
                    </div>

                    <div className="form-group" style={{ gridColumn: '2 / 3', background: 'rgba(16,185,129,0.04)', padding: '10px', borderRadius: '8px', border: '1px dashed rgba(16,185,129,0.3)' }}>
                      <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <HiOutlineDocumentText /> 🧾 Photo of 3rd Copy of Bill
                      </label>
                      <input
                        type="file"
                        ref={thirdBillPhotoRef}
                        className="form-input"
                        style={{ fontSize: '0.75rem', padding: '4px' }}
                        onChange={e => setThirdBillPhotoFile(e.target.files?.[0] || null)}
                        accept="image/*,application/pdf"
                      />
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Signed 3rd gate/transporter copy</span>
                    </div>

                    {/* Accounts Team Email Notification */}
                    <div className="form-group" style={{ gridColumn: '1 / 3', background: 'rgba(96,165,250,0.05)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(96,165,250,0.2)' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <HiOutlineMail /> Send Details to Accounts Team
                      </label>
                      <input
                        type="email"
                        className="form-input"
                        style={{ fontSize: '0.82rem', marginBottom: '6px' }}
                        placeholder="accounts@example.com (Accounts Email)"
                        value={form.accountsEmail || ''}
                        onChange={e => setForm({ ...form, accountsEmail: e.target.value })}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.76rem', color: 'var(--text-dim)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={form.notifyAccounts}
                          onChange={e => setForm({ ...form, notifyAccounts: e.target.checked })}
                        />
                        Send Driver, Vehicle & 3rd Bill/Material photo proofs to Accounts Team
                      </label>
                    </div>
                  </>
                ) : (
                  /* Invoice Upload for Inward */
                  <div className="form-group" style={{ gridColumn: '1 / 3' }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                      Upload Invoice / Challan Copy
                    </label>
                    <input
                      type="file"
                      ref={invoiceFileRef}
                      className="form-input"
                      onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
                      accept=".pdf,.png,.jpg,.jpeg"
                    />
                  </div>
                )}

                {/* Remarks */}
                <div className="form-group" style={{ gridColumn: '1 / 3' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Remarks / Gate Notes
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Comments, seal number, condition of goods..."
                    value={form.remarks || ''}
                    onChange={e => setForm({ ...form, remarks: e.target.value })}
                  />
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary btn-block" 
              disabled={submitting}
              style={{ marginTop: '20px', padding: '12px', fontSize: '0.95rem' }}
            >
              <HiOutlineSave /> {submitting ? 'Saving...' : (isInward ? 'Save & Dispatch Inward' : 'Save Outward Pass & Notify Accounts')}
            </button>
          </form>
        </div>

        {/* History / Logs */}
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
              <HiOutlineClipboardList style={{ color: 'var(--primary)' }} /> 
              {isInward ? 'Inward Gate Entries' : 'Outward Finished Goods Entries'}
            </h3>
            
            <div className="form-input-icon" style={{ width: '220px' }}>
              <HiOutlineSearch className="icon" />
              <input 
                type="text" 
                className="form-input" 
                style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }} 
                placeholder="Search Bill/Driver/Vehicle..." 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
              />
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading {activeTab} logs...
            </div>
          ) : entries.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)', background: 'var(--bg-input)', borderRadius: '8px' }}>
              No {activeTab} gate entries found.
            </div>
          ) : (
            <div className="azure-table-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>{isInward ? 'Bill No & Time' : 'Challan/Bill & Exit Time'}</th>
                    <th>{isInward ? 'Vendor' : 'Customer'}</th>
                    <th>Material & Qty</th>
                    <th>Vehicle & Driver</th>
                    <th>{isInward ? 'Document' : 'Photo Proofs (3rd Bill/Material)'}</th>
                    {!isInward && <th>Accounts Team</th>}
                    <th>Status</th>
                    {['super_admin', 'admin', 'gate_guard'].includes(user?.role) && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry._id}>
                      <td>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{entry.billNumber || 'N/A'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <HiOutlineClock /> 
                          {isInward ? (entry.entryTiming || new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })) : (entry.exitTiming || new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </div>
                      </td>

                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.vendorName || 'N/A'}</div>
                        {entry.vendorContact && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>📞 {entry.vendorContact}</div>
                        )}
                      </td>

                      <td>
                        <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{entry.materialType || 'N/A'}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 700 }}>
                          {entry.quantity || 0} {entry.unit || (isInward ? 'kg' : 'meters')}
                        </div>
                      </td>

                      <td>
                        <div style={{ fontWeight: 700, color: '#ffffff' }}>{entry.vehicleNumber || 'N/A'}</div>
                        {entry.driverName && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <HiOutlineUser /> {entry.driverName}
                          </div>
                        )}
                        {entry.driverPhone && (
                          <div style={{ fontSize: '0.72rem', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <HiOutlinePhone /> {entry.driverPhone}
                          </div>
                        )}
                      </td>

                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {/* Material Photo */}
                          {entry.materialPhotoUrl && (
                            <button
                              type="button"
                              onClick={() => setPreviewMedia({ url: entry.materialPhotoUrl, title: `Material / Vehicle Photo - ${entry.billNumber}` })}
                              style={{
                                background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#3b82f6',
                                borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '4px', textAlign: 'left'
                              }}
                            >
                              <HiOutlinePhotograph /> 📸 Material Photo
                            </button>
                          )}

                          {/* 3rd Bill Copy Photo */}
                          {entry.thirdBillPhotoUrl && (
                            <button
                              type="button"
                              onClick={() => setPreviewMedia({ url: entry.thirdBillPhotoUrl, title: `3rd Bill Copy Photo - ${entry.billNumber}` })}
                              style={{
                                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981',
                                borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '4px', textAlign: 'left'
                              }}
                            >
                              <HiOutlineDocumentText /> 🧾 3rd Bill Copy
                            </button>
                          )}

                          {/* Invoice Url */}
                          {entry.invoiceUrl && (
                            <a
                              href={formatFileUrl(entry.invoiceUrl)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b',
                                borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none',
                                display: 'inline-flex', alignItems: 'center', gap: '4px'
                              }}
                            >
                              <HiOutlineEye /> 📄 Invoice Doc
                            </a>
                          )}

                          {!entry.materialPhotoUrl && !entry.thirdBillPhotoUrl && !entry.invoiceUrl && (
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>No Attachments</span>
                          )}
                        </div>
                      </td>

                      {/* Accounts Notification status (Outward Only) */}
                      {!isInward && (
                        <td>
                          {entry.accountsEmailSentAt ? (
                            <div>
                              <span style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid #22c55e', borderRadius: '6px', padding: '3px 6px', fontSize: '0.72rem', fontWeight: 700, display: 'inline-block' }}>
                                ✓ Notified
                              </span>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                                {entry.accountsEmail || 'Accounts'}
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleNotifyAccounts(entry)}
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '3px 8px', fontSize: '0.72rem', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.4)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              title="Send Details to Accounts Team"
                            >
                              <HiOutlineMail /> Send to Accounts
                            </button>
                          )}
                        </td>
                      )}

                      <td>
                        <span className={`azure-badge ${
                          entry.status === 'pending' ? 'warning' :
                          entry.status === 'verified' ? 'running' : 'success'
                        }`}>
                          {entry.status === 'pending' ? 'Cleared Gate' :
                           entry.status === 'verified' ? 'Verified' : 'GRN Created'}
                        </span>
                      </td>

                      {['super_admin', 'admin', 'gate_guard'].includes(user?.role) && (
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {!isInward && (
                              <button
                                onClick={() => handleNotifyAccounts(entry)}
                                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '1.1rem' }}
                                title="Resend to Accounts Team"
                              >
                                <HiOutlineMail />
                              </button>
                            )}
                            <button 
                              onClick={() => handleDelete(entry._id)}
                              style={{ 
                                color: 'var(--danger, #ef4444)', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '1.1rem'
                              }}
                              title="Delete Gate Entry"
                            >
                              <HiOutlineTrash />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* MEDIA PREVIEW MODAL */}
      {previewMedia && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px'
        }} onClick={() => setPreviewMedia(null)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '14px', maxWidth: '750px', width: '100%',
            overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {previewMedia.title || 'Attachment Preview'}
              </h4>
              <button onClick={() => setPreviewMedia(null)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}>
                <HiOutlineX />
              </button>
            </div>

            <div style={{ padding: '20px', textAlign: 'center', maxHeight: '70vh', overflowY: 'auto' }}>
              <img src={formatFileUrl(previewMedia.url)} alt="Proof Preview" style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: '8px' }} />
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href={formatFileUrl(previewMedia.url)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                🔗 Open Original in New Tab
              </a>
              <button onClick={() => setPreviewMedia(null)} className="btn btn-primary btn-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WEBCAM & FILE E-BILL SCANNER MODAL */}
      <WebcamEBillScanner
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onScanComplete={handleEBillScanComplete}
      />

    </DashboardLayout>
  );
};

export default GateGuardDashboard;
